import { useState } from "react";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, AlertOctagon, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenshotDataUrl: string | null;
  onSubmit: (params: {
    description?: string;
    impact?: "critical" | "error" | "warning";
    screenshotDataUrl?: string | null;
  }) => Promise<void>;
  isSubmitting: boolean;
}

const IMPACT_OPTIONS = [
  { value: "critical" as const, label: "I can't continue working", icon: AlertOctagon, color: "border-destructive bg-destructive/10 text-destructive" },
  { value: "error" as const, label: "Something looks wrong", icon: AlertTriangle, color: "border-orange-500 bg-orange-50 text-orange-700" },
  { value: "warning" as const, label: "Minor issue", icon: Info, color: "border-blue-500 bg-blue-50 text-blue-700" },
] as const;

export function BugReportDialog({ open, onOpenChange, screenshotDataUrl, onSubmit, isSubmitting }: Props) {
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<"critical" | "error" | "warning">("error");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshotExpanded, setScreenshotExpanded] = useState(false);

  const handleSubmit = async () => {
    await onSubmit({
      description: description.trim() || undefined,
      impact,
      screenshotDataUrl: includeScreenshot ? screenshotDataUrl : null,
    });
    // Reset form
    setDescription("");
    setImpact("error");
    setIncludeScreenshot(true);
    setScreenshotExpanded(false);
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent size="md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Report an Issue</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            This helps us fix problems quickly. Technical details are captured automatically.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogBody className="space-y-4">
          {/* Screenshot preview */}
          {screenshotDataUrl && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Screenshot</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setIncludeScreenshot(!includeScreenshot)}
                >
                  {includeScreenshot ? "Exclude" : "Include"}
                </Button>
              </div>
              {includeScreenshot && (
                <button
                  type="button"
                  onClick={() => setScreenshotExpanded(!screenshotExpanded)}
                  className="block w-full rounded-md border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
                >
                  <img
                    src={screenshotDataUrl}
                    alt="Screenshot"
                    className={cn(
                      "w-full object-contain transition-all",
                      screenshotExpanded ? "max-h-[400px]" : "max-h-[120px]"
                    )}
                  />
                </button>
              )}
            </div>
          )}

          {/* Impact level */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How bad is it?</Label>
            <div className="grid grid-cols-1 gap-2">
              {IMPACT_OPTIONS.map((option) => {
                const Icon = option.icon;
                const selected = impact === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setImpact(option.value)}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm text-left transition-all",
                      selected ? option.color : "border-border hover:border-muted-foreground/30"
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className={selected ? "font-medium" : "text-muted-foreground"}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="bug-description" className="text-sm font-medium">
              What happened? <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="bug-description"
              placeholder="Describe what you were trying to do and what went wrong..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Auto-capture indicator */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Technical details included automatically</span>
          </div>
        </ResponsiveDialogBody>

        <ResponsiveDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Report"
            )}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
